import axios from 'axios';

const N8N_API_URL = 'https://aai.bigg.co.uk/webhook';

export async function createPost(connection, content) {
  try {
    console.log('Creating WordPress post...');
    
    // Prepare post data with defaults
    const post = {
      status: 'draft',
      ...content,
      content: {
        raw: content.content,
        rendered: content.content
      }
    };

    console.log('Request details:', {
      url: `${N8N_API_URL}/wordpress-create-post`,
      credentials: {
        username: connection.settings.username,
        // Hide most of the password
        applicationPassword: connection.settings.applicationPassword.substring(0, 4) + '...'
      },
      content: post
    });

    const response = await axios.post(`${N8N_API_URL}/wordpress-create-post`, {
      siteUrl: connection.settings.siteUrl,
      username: connection.settings.username,
      applicationPassword: connection.settings.applicationPassword,
      content: post
    });

    console.log('Post creation response:', response.data);
    
    if (!response.data.id) {
      throw new Error('No post ID returned from WordPress');
    }

    return {
      success: true,
      data: response.data,
      postId: response.data.id,
      permalink: response.data.link
    };
  } catch (error) {
    console.error('Error creating WordPress post:', error.response?.data || error);
    
    // Handle specific WordPress errors
    if (error.response?.data?.code === 'rest_cannot_create') {
      throw new Error(
        'WordPress permissions error: The application password does not have permission to create posts. ' +
        'Please create a new application password with full post management permissions.'
      );
    }
    
    if (error.response?.status === 401) {
      throw new Error(
        'Authentication failed: Please verify your username and application password. ' +
        'Make sure the application password is copied exactly as provided by WordPress.'
      );
    }

    throw error;
  }
}

export async function testConnection(credentials) {
  console.log('Testing WordPress connection...');
  
  try {
    // 1. Get site info and favicon
    const siteInfo = await getSiteInfo(credentials.siteUrl);
    console.log('Site info retrieved:', siteInfo);

    // 2. Test basic connection through N8N
    console.log('Testing basic connection...');
    const basicTestResponse = await axios.post(`${N8N_API_URL}/wordpress-test-connection`, {
      siteUrl: credentials.siteUrl,
      username: credentials.username,
      applicationPassword: credentials.applicationPassword
    });

    console.log('Basic test response:', basicTestResponse.data);

    // 3. Try creating a test post
    console.log('Creating test post...');
    const testPost = {
      title: 'Connection Test Post',
      content: 'This is a test post to verify the WordPress connection. It will be deleted automatically.',
      status: 'draft'
    };

    const createResponse = await axios.post(`${N8N_API_URL}/wordpress-create-post`, {
      siteUrl: credentials.siteUrl,
      username: credentials.username,
      applicationPassword: credentials.applicationPassword,
      content: testPost
    });

    console.log('Create test post response:', createResponse.data);

    if (!createResponse.data.id) {
      throw new Error('Failed to create test post');
    }

    // 4. Delete the test post
    console.log('Deleting test post...');
    const deleteResponse = await axios.post(`${N8N_API_URL}/wordpress-delete-post`, {
      siteUrl: credentials.siteUrl,
      username: credentials.username,
      applicationPassword: credentials.applicationPassword,
      postId: createResponse.data.id
    });

    console.log('Delete test post response:', deleteResponse.data);

    // If we got here, all tests passed
    return {
      success: true,
      data: {
        name: siteInfo.name,
        pageImageUrl: siteInfo.faviconUrl,
        testResults: {
          basicConnection: true,
          createPost: true,
          deletePost: true
        }
      }
    };

  } catch (error) {
    console.error('WordPress connection test failed:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
}

export async function getSiteInfo(siteUrl) {
  try {
    // Get site info from WordPress API
    const response = await axios.get(`${siteUrl}/wp-json`);
    
    // Get domain for favicon
    const domain = new URL(siteUrl).hostname;
    
    // Use DuckDuckGo's favicon service (no CORS issues)
    const faviconUrl = `https://external-content.duckduckgo.com/ip3/${domain}.ico`;

    return {
      name: response.data.name,
      url: siteUrl,
      description: response.data.description,
      faviconUrl: faviconUrl
    };
  } catch (error) {
    console.error('Error getting WordPress site info:', error);
    throw error;
  }
} 